import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { apiRequest } from '@/lib/queryClient';
import {
  FEATURE_FOLD,
  HERO_IMAGE,
  checkHeroImageFit,
  isSaveableFeature,
  newFeatureDraft,
  nextClientKey,
  normalizeFeaturesForSave,
  type FeatureDraft,
  type HeroImageFit,
} from '@/lib/productUpdateForm';
import { ProductUpdatePopupView, type PopupUpdate } from '@/components/product-updates/ProductUpdatePopupView';
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  GripVertical,
  ExternalLink,
  Image as ImageIcon,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/** Format a Date as a local-time string for datetime-local inputs (YYYY-MM-DDTHH:MM) */
function toDatetimeLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}

// Types
interface Feature {
  id: number;
  title: string;
  description: string;
  learnMoreUrl: string | null;
  sortOrder: number;
}

interface ProductUpdate {
  id: number;
  version: string;
  releaseDate: string;
  headline: string;
  heroImageUrl: string | null;
  heroDescription: string;
  status: 'draft' | 'published' | 'expired';
  publishDate: string | null;
  createdById: number | null;
  createdAt: string;
  updatedAt: string;
  features: Feature[];
}

type UpdateFormData = {
  version: string;
  releaseDate: string;
  headline: string;
  heroImageUrl: string | null;
  heroDescription: string;
  status: 'draft' | 'published' | 'expired';
  publishDate: string | null;
  features: FeatureDraft[];
};

const makeEmptyForm = (): UpdateFormData => ({
  version: '',
  releaseDate: new Date().toISOString().split('T')[0],
  headline: '',
  heroImageUrl: null,
  heroDescription: '',
  status: 'draft',
  publishDate: null,
  features: [newFeatureDraft(0)],
});

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  expired: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
};

const HERO_MAX_MB = Math.round(HERO_IMAGE.maxBytes / (1024 * 1024));

// ─── Sortable feature row ────────────────────────────────────────────────────

interface SortableFeatureRowProps {
  feature: FeatureDraft;
  idx: number;
  total: number;
  reducedMotion: boolean;
  /** True on the last row members see without scrolling, when more saveable rows follow. */
  showFoldMarker: boolean;
  onChange: (field: keyof FeatureDraft, value: string | null) => void;
  onRemove: () => void;
}

function SortableFeatureRow({ feature, idx, total, reducedMotion, showFoldMarker, onChange, onRemove }: SortableFeatureRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: feature.clientKey });

  // Vertical-only: zero the x component instead of pulling in @dnd-kit/modifiers.
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
    transition: reducedMotion ? undefined : transition,
  };

  // The fold marker lives inside the sortable node (not as a sibling) so the
  // vertical sorting strategy's gap math stays consistent while dragging.

  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && 'relative z-10')}>
      <div
        className={cn(
          'border rounded-lg p-4 space-y-3 bg-white dark:bg-gray-800',
          isDragging && 'shadow-lg ring-2 ring-orange-300 opacity-90',
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <button
              type="button"
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              aria-label={`Reorder feature ${idx + 1} of ${total}`}
              title="Drag, or focus and use space then arrow keys, to reorder"
              className="touch-none rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            Feature {idx + 1}
          </div>
          {total > 1 && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove feature ${idx + 1}`}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Input
          placeholder="Feature title"
          value={feature.title}
          onChange={(e) => onChange('title', e.target.value)}
        />
        <Textarea
          placeholder="Feature description"
          rows={2}
          value={feature.description}
          onChange={(e) => onChange('description', e.target.value)}
        />
        <div className="flex items-center gap-2">
          <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Learn more URL (optional)"
            value={feature.learnMoreUrl ?? ''}
            onChange={(e) => onChange('learnMoreUrl', e.target.value || null)}
            className="text-sm"
          />
        </div>
      </div>
      {showFoldMarker && (
        <div className="mt-3 flex items-center gap-3 text-xs font-medium text-amber-700 dark:text-amber-400">
          <div className="h-px flex-1 bg-amber-300 dark:bg-amber-700" />
          <span>Members scroll to see features below this line</span>
          <div className="h-px flex-1 bg-amber-300 dark:bg-amber-700" />
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminProductUpdatesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const reducedMotion = usePrefersReducedMotion();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<UpdateFormData>(makeEmptyForm);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [previewOpen, setPreviewOpen] = useState(false);
  // Deferred upload: hold the file locally until Save
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
  const [heroFit, setHeroFit] = useState<HeroImageFit | null>(null);
  const [heroLoadFailed, setHeroLoadFailed] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Fetch all updates
  const { data: updates = [], isLoading } = useQuery<ProductUpdate[]>({
    queryKey: ['/api/product-updates/admin', statusFilter],
    queryFn: () =>
      apiRequest(
        'GET',
        statusFilter === 'all'
          ? '/api/product-updates/admin'
          : `/api/product-updates/admin?status=${statusFilter}`,
      ),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: unknown) => apiRequest('POST', '/api/product-updates/admin', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/product-updates/admin'] });
      toast({ title: 'Product update created successfully' });
      closeDialog();
    },
    onError: (err: any) => {
      toast({ title: 'Failed to create update', description: err.message, variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: unknown }) =>
      apiRequest('PATCH', `/api/product-updates/admin/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/product-updates/admin'] });
      toast({ title: 'Product update saved successfully' });
      closeDialog();
    },
    onError: (err: any) => {
      toast({ title: 'Failed to save update', description: err.message, variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/product-updates/admin/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/product-updates/admin'] });
      toast({ title: 'Product update deleted' });
    },
    onError: (err: any) => {
      toast({ title: 'Failed to delete update', description: err.message, variant: 'destructive' });
    },
  });

  const resetHeroState = () => {
    setHeroFit(null);
    setHeroLoadFailed(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(makeEmptyForm());
    resetHeroState();
    setDialogOpen(true);
  };

  const openEdit = (update: ProductUpdate) => {
    setEditingId(update.id);
    setForm({
      version: update.version,
      releaseDate: new Date(update.releaseDate).toISOString().split('T')[0],
      headline: update.headline,
      heroImageUrl: update.heroImageUrl,
      heroDescription: update.heroDescription,
      status: update.status,
      publishDate: update.publishDate ? toDatetimeLocal(new Date(update.publishDate)) : null,
      features:
        update.features.length > 0
          ? update.features.map((f) => ({ ...f, clientKey: nextClientKey() }))
          : [newFeatureDraft(0)],
    });
    resetHeroState();
    setDialogOpen(true);
  };

  const closeDialog = () => {
    // Revoke blob URL to prevent memory leak
    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    setPendingImageFile(null);
    setPendingImagePreview(null);
    setPreviewOpen(false);
    setDialogOpen(false);
    setEditingId(null);
    setForm(makeEmptyForm());
    resetHeroState();
  };

  const handleSubmit = async () => {
    if (!form.version.trim() || !form.headline.trim() || !form.heroDescription.trim()) {
      toast({ title: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    // If there's a pending image file, upload it first
    let heroImageUrl = form.heroImageUrl;
    if (pendingImageFile) {
      setUploading(true);
      try {
        heroImageUrl = await uploadPendingImage();
      } catch {
        toast({ title: 'Failed to upload image', variant: 'destructive' });
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    // Drop empty rows, renumber sortOrder from position, strip client keys
    const cleanFeatures = normalizeFeaturesForSave(form.features);
    // Convert local datetime-local value to ISO string for unambiguous UTC on the backend
    const normalizedPublishDate = form.publishDate ? new Date(form.publishDate).toISOString() : null;
    const payload = { ...form, heroImageUrl, features: cleanFeatures, publishDate: normalizedPublishDate };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const addFeature = () => {
    setForm((prev) => ({
      ...prev,
      features: [...prev.features, newFeatureDraft(prev.features.length)],
    }));
  };

  const removeFeature = (clientKey: string) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features
        .filter((f) => f.clientKey !== clientKey)
        .map((f, i) => ({ ...f, sortOrder: i })),
    }));
  };

  const updateFeature = (clientKey: string, field: keyof FeatureDraft, value: string | null) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.map((f) => (f.clientKey === clientKey ? { ...f, [field]: value } : f)),
    }));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setForm((prev) => {
      const from = prev.features.findIndex((f) => f.clientKey === active.id);
      const to = prev.features.findIndex((f) => f.clientKey === over.id);
      if (from < 0 || to < 0) return prev;
      return {
        ...prev,
        features: arrayMove(prev.features, from, to).map((f, i) => ({ ...f, sortOrder: i })),
      };
    });
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > HERO_IMAGE.maxBytes) {
      toast({
        title: 'Image too large',
        description: `Hero images must be ${HERO_MAX_MB} MB or smaller.`,
        variant: 'destructive',
      });
      return;
    }

    // Revoke previous blob URL if any
    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);

    // Store file for deferred upload on Save; show local preview
    setPendingImageFile(file);
    setPendingImagePreview(URL.createObjectURL(file));
    resetHeroState();
    // Clear any existing remote URL so the preview shows the local file
    setForm((prev) => ({ ...prev, heroImageUrl: null }));
  };

  const clearImage = () => {
    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    setPendingImageFile(null);
    setPendingImagePreview(null);
    resetHeroState();
    setForm((prev) => ({ ...prev, heroImageUrl: null }));
  };

  /** Upload the pending image file to storage. Returns the public URL. */
  const uploadPendingImage = async (): Promise<string> => {
    if (!pendingImageFile) throw new Error('No pending image');
    const body = new FormData();
    body.append('image', pendingImageFile);

    const res = await fetch('/api/product-updates/admin/upload-hero', {
      method: 'POST',
      body,
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Image upload failed');
    const data = await res.json();
    return data.url as string;
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const heroSrc = pendingImagePreview ?? form.heroImageUrl;

  const rowCount = form.features.length;
  // Empty rows are dropped on save and never reach members, so the fold is
  // derived from saveable rows only. foldRowKey marks the fifth saveable row.
  const { saveableCount, foldRowKey } = useMemo(() => {
    let seen = 0;
    let key: string | null = null;
    for (const f of form.features) {
      if (!isSaveableFeature(f)) continue;
      seen += 1;
      if (seen === FEATURE_FOLD) key = f.clientKey;
    }
    return { saveableCount: seen, foldRowKey: seen > FEATURE_FOLD ? key : null };
  }, [form.features]);
  const belowFold = Math.max(0, saveableCount - FEATURE_FOLD);

  // Preview: current unsaved form state, same filtering as save, in current order.
  const previewUpdate: PopupUpdate = useMemo(
    () => ({
      version: form.version,
      releaseDate: form.releaseDate,
      headline: form.headline || 'Untitled update',
      heroImageUrl: heroSrc,
      heroDescription: form.heroDescription,
      features: normalizeFeaturesForSave(form.features).map((f, i) => ({
        ...f,
        id: f.id ?? `draft-${i}`,
      })),
    }),
    [form, heroSrc],
  );

  return (
    <AdminLayout
      title="Product Updates"
      description="Create and manage product feature update announcements shown to users."
    >
      {/* Header bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          New Update
        </Button>
      </div>

      {/* Updates list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
        </div>
      ) : updates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ImageIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No product updates yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Create your first product update to announce new features to users.</p>
            <Button onClick={openCreate} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Create First Update
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {updates.map((update) => (
            <Card
              key={update.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openEdit(update)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <Badge className={cn('text-xs font-medium', statusColors[update.status])}>
                        {update.status.charAt(0).toUpperCase() + update.status.slice(1)}
                      </Badge>
                      <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
                        v{update.version}
                      </span>
                      <span className="text-sm text-gray-400 dark:text-gray-500">
                        {new Date(update.releaseDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {update.headline}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                      {update.heroDescription}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {update.features.length} feature{update.features.length !== 1 ? 's' : ''}
                      </span>
                      {update.publishDate && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          · Publish: {new Date(update.publishDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(update);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Delete"
                      aria-label="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete "${update.headline}"? This cannot be undone.`)) {
                          deleteMutation.mutate(update.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Product Update' : 'New Product Update'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Modify this product update. Changes are saved when you click Save; Cancel discards them.'
                : 'Create a new product feature update announcement.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Core Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  placeholder="e.g. 2.4.0"
                  value={form.version}
                  onChange={(e) => setForm((prev) => ({ ...prev, version: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="releaseDate">Release Date *</Label>
                <Input
                  id="releaseDate"
                  type="date"
                  value={form.releaseDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, releaseDate: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="headline">Headline *</Label>
              <Input
                id="headline"
                placeholder="e.g. Meet the New Dashboard"
                value={form.headline}
                onChange={(e) => setForm((prev) => ({ ...prev, headline: e.target.value }))}
              />
            </div>

            {/* Hero Section */}
            <div className="border rounded-lg p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Hero Section</h4>

              <div>
                <Label>Hero Image</Label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
                  Shown as a full-width banner at the top of the popup. Recommended{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {HERO_IMAGE.width} × {HERO_IMAGE.height} px ({HERO_IMAGE.ratioLabel})
                  </span>
                  {' · '}{HERO_IMAGE.acceptedLabel}{' · '}up to {HERO_MAX_MB} MB
                </p>
                <div className="flex flex-wrap items-start gap-3">
                  {heroSrc && (
                    <div className="space-y-2">
                      {/* Same ratio and object-fit as the member popup, so this IS the crop they see */}
                      <div className="relative w-64 aspect-video rounded-lg overflow-hidden border bg-white dark:bg-gray-800">
                        {heroLoadFailed ? (
                          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                            Image not found
                          </div>
                        ) : (
                          <img
                            src={heroSrc}
                            alt="Hero preview, cropped as members will see it"
                            className="w-full h-full object-cover"
                            onLoad={(e) => {
                              const img = e.currentTarget;
                              setHeroFit(checkHeroImageFit(img.naturalWidth, img.naturalHeight));
                            }}
                            onError={() => setHeroLoadFailed(true)}
                          />
                        )}
                        <button
                          type="button"
                          onClick={clearImage}
                          aria-label="Remove hero image"
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                      {heroFit && heroFit.status === 'ok' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {heroFit.width} × {heroFit.height} px · fits the {HERO_IMAGE.ratioLabel} banner
                        </p>
                      )}
                    </div>
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed rounded-lg cursor-pointer text-sm text-gray-500 hover:border-orange-400 hover:text-orange-600 transition-colors">
                    <Upload className="h-4 w-4" />
                    {heroSrc ? 'Replace Image' : 'Upload Image'}
                    <input
                      type="file"
                      accept={HERO_IMAGE.acceptedTypes}
                      className="hidden"
                      onChange={handleImageSelect}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {heroFit?.message && (
                  <div
                    role="status"
                    className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                  >
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{heroFit.message} You can still save.</span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="heroDescription">Hero Description *</Label>
                <Textarea
                  id="heroDescription"
                  placeholder="Brief description of this release..."
                  rows={3}
                  value={form.heroDescription}
                  onChange={(e) => setForm((prev) => ({ ...prev, heroDescription: e.target.value }))}
                />
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Feature Highlights</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-testid="feature-count">
                    {saveableCount} feature{saveableCount !== 1 ? 's' : ''} will be saved
                    {rowCount !== saveableCount && ` · ${rowCount - saveableCount} empty row${rowCount - saveableCount !== 1 ? 's' : ''} will be dropped`}
                    {belowFold > 0
                      ? ` · members see the first ${FEATURE_FOLD} without scrolling, ${belowFold} below the fold`
                      : saveableCount > 1
                        ? ' · all visible without scrolling'
                        : ''}
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addFeature}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Feature
                </Button>
              </div>

              <DndContext
                id="product-update-features"
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={form.features.map((f) => f.clientKey)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {form.features.map((feature, idx) => (
                      <SortableFeatureRow
                        key={feature.clientKey}
                        feature={feature}
                        idx={idx}
                        total={form.features.length}
                        reducedMotion={reducedMotion}
                        showFoldMarker={feature.clientKey === foldRowKey}
                        onChange={(field, value) => updateFeature(feature.clientKey, field, value)}
                        onRemove={() => removeFeature(feature.clientKey)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            {/* Publishing Controls */}
            <div className="border rounded-lg p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Publishing Controls</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={(val) =>
                      setForm((prev) => ({ ...prev, status: val as UpdateFormData['status'] }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="publishDate">Publish Date/Time</Label>
                  <Input
                    id="publishDate"
                    type="datetime-local"
                    value={form.publishDate ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, publishDate: e.target.value || null }))
                    }
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave empty to publish immediately when status is Published</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)} disabled={isSaving}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSaving || uploading}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {uploading ? 'Uploading image...' : isSaving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Update'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview: the real member popup, rendered from unsaved form state. Fires no mutation. */}
      {dialogOpen && (
        <ProductUpdatePopupView
          open={previewOpen}
          update={previewUpdate}
          onDismiss={() => setPreviewOpen(false)}
          previewMode
        />
      )}
    </AdminLayout>
  );
}
